import * as S from './HomePageWithData.styles';

import React, {useCallback, useEffect, useState} from 'react';
import classNames from 'classnames';
import {History} from 'history';

import {UserDetails} from '../components/HomePage/graphql';
import ProjectsList from '../components/HomePage/ProjectsList';
import ResourcesList from '../components/HomePage/ResourcesList';
import TeamsList from '../components/HomePage/TeamsList';
import PageWithSidebar from '../components/PageWithSidebar';
import ReportsTable from '../components/ReportsTable';
import RunsTable from '../components/RunsTable';
import {Link} from 'react-router-dom';
import {
  useUpdateEntityMutation,
  useViewerQuery,
  ViewerRunsQueryResult,
} from '../generated/graphql';
import DeferredQueryData from '../containers/DeferredQueryData';
import {
  ONBOARDING_RESOURCES_POPUP_FEATURE_KEY,
  TableRowsContextResult,
  useOnboardingContext,
} from '../util/userContext';
import makeComp from '../util/profiler';
import ExampleShowcase from '../components/ExampleShowcase';

import DocsIcon from '../assets/ic-docs.svg';
import ConfigurationIcon from '../assets/ic-configuration.svg';
import LightbulbIcon from '../assets/ic-lightbulb.svg';
import {TargetBlank} from '../util/links';
import {CreateTeamModal} from '../components/CreateTeamModal';
import config from '../config';

interface HomePageWithDataProps {
  data: UserDetails;
  history: History;
  refetch: () => Promise<any>;
  runsQuery: ViewerRunsQueryResult;
  tableRowsContext: TableRowsContextResult;
}

const HomePageWithData: React.FC<HomePageWithDataProps> = makeComp(
  ({data, history, refetch, runsQuery, tableRowsContext}) => {
    const viewerQuery = useViewerQuery();
    const userEntity = viewerQuery.data?.viewer?.userEntity;
    const [updateEntity] = useUpdateEntityMutation();
    const [codeSavingEnabled, setCodeSavingEnabled] = useState(false);
    useEffect(() => {
      const enabled = userEntity?.codeSavingEnabled;
      if (enabled != null) {
        setCodeSavingEnabled(enabled);
      }
    }, [userEntity]);

    const {onboardingState, setOnboardingState} = useOnboardingContext(
      ONBOARDING_RESOURCES_POPUP_FEATURE_KEY
    );

    const {
      state: tableRowsByPage,
      setState: setTableRowsByPage,
    } = tableRowsContext;
    const setTableRows = useCallback(
      (key: string, rows: number) => {
        setTableRowsByPage({
          ...tableRowsByPage,
          home: {
            ...tableRowsByPage.home,
            [key]: rows,
          },
        });
      },
      [tableRowsByPage, setTableRowsByPage]
    );

    const projects = data.projects;
    const reports = data.views;

    const firstRunLogged =
      (runsQuery.data?.viewer?.runs ?? {edges: []}).edges.length > 0;
    const showOnboarding = !runsQuery.loading && !firstRunLogged;

    const exampleProjectSection = (
      <S.Section>
        <S.SectionHeader>Example project</S.SectionHeader>
        <S.SectionText>Try visualizing results in 2 minutes.</S.SectionText>
        <S.SectionButton
          primary
          href="http://wandb.me/intro"
          target="_blank"
          rel="noopener noreferrer">
          Try example
        </S.SectionButton>
      </S.Section>
    );

    const codeSavingSection = (
      <S.Section grow>
        <S.SectionHeader>Save snapshots of your code</S.SectionHeader>
        <S.SectionText>Capture code to reproduce models.</S.SectionText>
        <S.SectionCheckboxContainer>
          <S.SectionCheckbox
            toggle
            checked={codeSavingEnabled}
            onChange={async () => {
              if (userEntity == null) {
                return;
              }
              setCodeSavingEnabled(!codeSavingEnabled);
              await updateEntity({
                variables: {
                  entityName: userEntity.name,
                  codeSavingEnabled: !codeSavingEnabled,
                },
              });
              await viewerQuery.refetch();
            }}
          />
          Enable code saving
        </S.SectionCheckboxContainer>
      </S.Section>
    );

    const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false);
    const openCreateTeamModal = useCallback(
      () => setCreateTeamModalOpen(true),
      []
    );
    const closeCreateTeamModal = useCallback(
      () => setCreateTeamModalOpen(false),
      []
    );

    const inviteTeamSection = (
      <S.Section grow>
        <S.SectionHeader>Invite your team</S.SectionHeader>
        <S.SectionText>Collaborate seamlessly on projects.</S.SectionText>
        <S.SectionButton onClick={openCreateTeamModal}>
          {(data.organizations ?? []).length > 0
            ? 'Create team'
            : 'Invite team'}
        </S.SectionButton>
        <CreateTeamModal
          open={createTeamModalOpen}
          onClose={closeCreateTeamModal}
          orgOptions={(data.organizations ?? []).map(o => ({
            text: o.name,
            value: o.id,
          }))}
          onCreate={teamName => history.push(`/${teamName}`)}
        />
      </S.Section>
    );

    const exampleShowcaseSection = (
      <S.Section grow>
        <S.SectionHeader>Try your own project</S.SectionHeader>
        <S.SectionText>
          Add a few lines of code to get a live dashboard of your model
          training, including logs and system metrics.
        </S.SectionText>
        <ExampleShowcase />
      </S.Section>
    );

    const hideSidebarInMobile =
      projects.edges.length === 0 &&
      data.teams.edges.length <= 1 &&
      showOnboarding;

    return (
      <PageWithSidebar className="home-page">
        <div
          className={classNames('sidebar', {
            'mobile-hide': hideSidebarInMobile,
          })}>
          <S.SidebarHeader as="h1">Home</S.SidebarHeader>
          <ProjectsList
            name="Projects"
            data={projects}
            entityName={data.defaultEntity?.name ?? data.username}
          />
          {data.teams.edges.length > 0 && (
            <TeamsList
              name="Teams"
              data={data.teams}
              userQueryRefetch={refetch}
            />
          )}
          <ResourcesList />
          <S.SidebarBlock>
            <S.AlertPopup
              unmountWhenClosed
              open={!onboardingState.onboardingResourcesPopup}
              y={0}
              text="Onboarding resources moved here."
              closeButton
              onClose={() =>
                setOnboardingState({
                  ...onboardingState,
                  onboardingResourcesPopup: true,
                })
              }
            />
            <S.SidebarBlockHeader>Quickstart</S.SidebarBlockHeader>
            <S.SidebarBlockLink>
              <S.SidebarBlockLinkIcon src={DocsIcon} />
              <TargetBlank href="http://wandb.me/intro">
                Example project
              </TargetBlank>
            </S.SidebarBlockLink>
            <S.SidebarBlockLink>
              <S.SidebarBlockLinkIcon src={ConfigurationIcon} />
              <Link to="/settings">Settings</Link>
            </S.SidebarBlockLink>
            <S.SidebarBlockLink>
              <S.SidebarBlockLinkIcon src={LightbulbIcon} />
              <TargetBlank href="https://docs.wandb.ai/library">
                Integrations
              </TargetBlank>
            </S.SidebarBlockLink>
          </S.SidebarBlock>
        </div>
        <div className="main-page">
          {showOnboarding && (
            <S.OnboardingContainer data-test="home-onboarding">
              <S.HomeHeader>
                <S.HomeHeaderMainText>
                  Get started with Weights & Biases
                </S.HomeHeaderMainText>
                <S.HomeHeaderSubText as="h2">
                  Run a machine learning experiment logging results to W&B and
                  see live results in your dashboard in 5 minutes.
                </S.HomeHeaderSubText>
              </S.HomeHeader>
              <S.SectionContainer>
                <S.SectionColumn>
                  {exampleProjectSection}
                  {codeSavingSection}
                  {!config.ENVIRONMENT_IS_PRIVATE && inviteTeamSection}
                </S.SectionColumn>
                <S.SectionColumn>{exampleShowcaseSection}</S.SectionColumn>
              </S.SectionContainer>
              <S.MobileSectionContainer>
                {exampleProjectSection}
                {exampleShowcaseSection}
                {codeSavingSection}
                {!config.ENVIRONMENT_IS_PRIVATE && inviteTeamSection}
              </S.MobileSectionContainer>
            </S.OnboardingContainer>
          )}
          <div>
            <DeferredQueryData query={runsQuery}>
              {runsData => {
                const runs = runsData?.viewer?.runs ?? {
                  edges: [],
                };
                if (runs.edges.length === 0) {
                  return null;
                }
                return (
                  <RunsTable
                    name="Runs"
                    data={runs}
                    showUserColumn={false}
                    pageSize={tableRowsByPage.home?.runs}
                    onChangePageSize={(pageSize: number) =>
                      setTableRows('runs', pageSize)
                    }
                  />
                );
              }}
            </DeferredQueryData>
            {reports.edges.length > 0 && (
              <ReportsTable
                data={reports}
                pageSize={tableRowsByPage.home?.reports}
                onChangePageSize={(pageSize: number) =>
                  setTableRows('reports', pageSize)
                }
              />
            )}
          </div>
        </div>
      </PageWithSidebar>
    );
  },
  {id: 'HomePageWithData', memo: true}
);

export default HomePageWithData;
